// AdministrativeAreas mutations — create / rename / move (reparent) / disable+reactivate / delete /
// reorder. Each invalidates the one project-keyed administrativeAreas query on success
// (mirrors useZoneMutations). All endpoints are `reference_data:manage`-gated +
// active-project on the backend; the UI gates the affordances with useCan.

import { useMutation, useQueryClient, type UseMutationResult } from '@tanstack/react-query';
import { apiClient } from '../lib/apiClient.js';
import { administrativeAreasKeys } from './useAdministrativeAreas.js';
import type {
  CreateAdministrativeAreaRequest,
  MoveAdministrativeAreaRequest,
  UpdateAdministrativeAreaRequest,
} from '@ems-app/schemas/administrative-areas/requests';
import type { CreatedAdministrativeAreaResponse } from '@ems-app/schemas/administrative-areas/responses';
import { refreshOnSettled } from '../lib/settled.js';

function useInvalidateAdministrativeAreas(projectId: string): () => void {
  const qc = useQueryClient();
  return () => void qc.invalidateQueries({ queryKey: administrativeAreasKeys.all(projectId) });
}

export function useCreateAdministrativeArea(
  projectId: string,
): UseMutationResult<CreatedAdministrativeAreaResponse, Error, CreateAdministrativeAreaRequest> {
  const invalidate = useInvalidateAdministrativeAreas(projectId);
  return useMutation({
    mutationFn: async (body) =>
      (await apiClient.post<CreatedAdministrativeAreaResponse>(`/projects/${projectId}/administrative-areas`, body)).data,
    onSuccess: invalidate,
  });
}

export function useRenameAdministrativeArea(
  projectId: string,
): UseMutationResult<void, Error, { administrativeAreaId: number; body: UpdateAdministrativeAreaRequest }> {
  const invalidate = useInvalidateAdministrativeAreas(projectId);
  return useMutation({
    mutationFn: async ({ administrativeAreaId, body }) => {
      await apiClient.patch(`/projects/${projectId}/administrative-areas/${administrativeAreaId}`, body);
    },
    onSuccess: invalidate,
  });
}

/** PUT …/administrative-areas/:id/parent — the move; the body carries the MOVED area's stamp (its own row is written). */
export function useMoveAdministrativeArea(
  projectId: string,
): UseMutationResult<void, Error, { administrativeAreaId: number; body: MoveAdministrativeAreaRequest }> {
  const invalidate = useInvalidateAdministrativeAreas(projectId);
  return useMutation({
    mutationFn: async ({ administrativeAreaId, body }) => {
      await apiClient.put(`/projects/${projectId}/administrative-areas/${administrativeAreaId}/parent`, body);
    },
    onSuccess: invalidate,
  });
}

export function useSetAdministrativeAreaDisabled(
  projectId: string,
): UseMutationResult<void, Error, { administrativeAreaId: number; disabled: boolean }> {
  const invalidate = useInvalidateAdministrativeAreas(projectId);
  return useMutation({
    mutationFn: async ({ administrativeAreaId, disabled }) => {
      await apiClient.post(
        `/projects/${projectId}/administrative-areas/${administrativeAreaId}/${disabled ? 'disable' : 'reactivate'}`,
      );
    },
    onSettled: refreshOnSettled(invalidate),
  });
}

export function useReorderAdministrativeAreas(
  projectId: string,
): UseMutationResult<void, Error, { parentId: number | null; orderedIds: number[]; country?: string }> {
  const invalidate = useInvalidateAdministrativeAreas(projectId);
  return useMutation({
    mutationFn: async ({ parentId, orderedIds, country }) => {
      await apiClient.post(`/projects/${projectId}/administrative-areas/reorder`, {
        parent_id: parentId,
        country_iso_code: country, // a null-parent (root) reorder scopes to this country
        orderedIds,
      });
    },
    onSettled: refreshOnSettled(invalidate),
  });
}

/** DELETE …/administrative-areas/:id — only while nothing uses it; a refusal (409 REFERENCE_IN_USE) names the uses, read back by
 *  `ReferenceDeleteDialog`. */
export function useDeleteAdministrativeArea(projectId: string): UseMutationResult<void, Error, number> {
  const invalidate = useInvalidateAdministrativeAreas(projectId);
  return useMutation({
    mutationFn: async (id) => {
      await apiClient.delete(`/projects/${projectId}/administrative-areas/${id}`);
    },
    onSettled: refreshOnSettled(invalidate),
  });
}
